import { createContext, useContext, useState, useEffect } from "react";
import { auth, rtdb } from "../services/firebase";
import {
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    verifyPasswordResetCode,
    confirmPasswordReset,
} from "firebase/auth";
import { ref, set, get } from "firebase/database";

import { saveCart, loadCart, clearCart } from "../utils/cartStorage";

const AuthContext = createContext();

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const [cart, setCart] = useState(() => loadCart());
    const [searchBar, setSearchBar] = useState("");

    /* ======================
       CART PERSISTENCE
    ====================== */
    useEffect(() => {
        saveCart(cart);
    }, [cart]);

    /* ======================
       AUTH STATE
    ====================== */
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            try {
                if (!firebaseUser) {
                    setUser(null);
                    localStorage.removeItem("emmaUser");
                    setLoading(false);
                    return;
                }

                const uid = firebaseUser.uid;
                const email = firebaseUser.email || "";

                const snap = await get(ref(rtdb, `users/client/${uid}`));

                if (snap.exists()) {
                    const data = snap.val();

                    const normalizedUser = {
                        uid,
                        email: data.email || email,
                        firstName: data.firstName || "",
                        lastName: data.lastName || "",
                        phone: data.phone || "",
                        cpf: String(data.cpf) || "",
                        birthDate: data.birthDate || null,
                        role: data.role || "client",
                        createdAt: data.createdAt || null,
                        status: data.status || "active",
                    };

                    setUser(normalizedUser);
                    localStorage.setItem("emmaUser", JSON.stringify(normalizedUser));
                } else {
                    // fallback extremo (não deveria acontecer)
                    const fallbackUser = {
                        uid,
                        email,
                        firstName: "",
                        lastName: "",
                        phone: "",
                        cpf: "",
                        birthDate: null,
                        role: "client",
                        status: "active",
                        createdAt: null,
                    };

                    setUser(fallbackUser);
                    localStorage.setItem("emmaUser", JSON.stringify(fallbackUser));
                }

                setLoading(false);
            } catch (err) {
                console.error("Erro ao carregar usuário:", err);
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, []);

    /* ======================
       LOGIN
    ====================== */
    async function login(email, password) {
        try {
            await signInWithEmailAndPassword(auth, email, password);
            return { ok: true };
        } catch (error) {
            console.error("Erro no login:", error);
            return { ok: false, error };
        }
    }

    /* ======================
       LOGOUT
    ====================== */
    async function logout() {
        await signOut(auth);
        setUser(null);
        localStorage.removeItem("emmaUser");
    }

    /* ======================
       REGISTER (COMPLETO)
    ====================== */
    async function register({
        firstName,
        lastName,
        email,
        phone,
        cpf,
        password,
        birthDate,
    }) {
        try {
            const cred = await createUserWithEmailAndPassword(auth, email, password);
            const uid = cred.user.uid;

            await set(ref(rtdb, `users/client/${uid}`), {
                firstName,
                lastName,
                email,
                phone,
                cpf,
                birthDate,
                role: "client",
                status: "active",
                createdAt: Date.now(),
            });

            // onAuthStateChanged cuidará do setUser
            return { ok: true };
        } catch (error) {
            console.error("Erro no register:", error);
            return { ok: false, error };
        }
    }

    /* ======================
       PASSWORD RESET
    ====================== */
    function getPasswordResetActionUrl() {
        return `${window.location.origin}/redefinir-senha`;
    }

    async function requestPasswordReset(email) {
        const trimmed = String(email || "").trim().toLowerCase();

        if (!trimmed) {
            return { ok: false, error: { code: "invalid-email" } };
        }

        try {
            auth.languageCode = "pt-BR";

            await sendPasswordResetEmail(auth, trimmed, {
                url: getPasswordResetActionUrl(),
                handleCodeInApp: true,
            });
            return { ok: true };
        } catch (error) {
            console.error("Erro ao solicitar reset de senha:", error);

            if (error?.code === "auth/invalid-email") {
                return { ok: false, error };
            }

            if (error?.code === "auth/too-many-requests") {
                return {
                    ok: false,
                    error,
                    message: "Muitas tentativas. Aguarde alguns minutos e tente novamente.",
                };
            }

            // Evita enumeração de e-mails: resposta genérica de sucesso
            return { ok: true };
        }
    }

    async function verifyResetCode(oobCode) {
        try {
            const email = await verifyPasswordResetCode(auth, oobCode);
            return { ok: true, email };
        } catch (error) {
            console.error("Erro ao validar link de reset:", error);
            return { ok: false, error };
        }
    }

    async function completePasswordReset(oobCode, newPassword) {
        try {
            if (auth.currentUser) {
                await signOut(auth);
                setUser(null);
                localStorage.removeItem("emmaUser");
            }

            await confirmPasswordReset(auth, oobCode, newPassword);
            return { ok: true };
        } catch (error) {
            console.error("Erro ao redefinir senha:", error);
            return { ok: false, error };
        }
    }

    /* ======================
       CART CORE
    ====================== */
    function addToCart({ productId, flavor, quantity }) {
        if (!productId || !flavor || quantity < 1) return;

        setCart((prev) => {
            const index = prev.findIndex(
                (item) => item.productId === productId && item.flavor === flavor
            );

            if (index >= 0) {
                const updated = [...prev];
                updated[index].quantity += quantity;
                return updated;
            }

            return [...prev, { productId, flavor, quantity }];
        });
    }

    function removeFromCart(productId, flavor) {
        setCart((prev) =>
            prev.filter(
                (item) => !(item.productId === productId && item.flavor === flavor)
            )
        );
    }

    function updateQuantity(productId, flavor, quantity) {
        if (quantity < 1) return;

        setCart((prev) =>
            prev.map((item) =>
                item.productId === productId && item.flavor === flavor
                    ? { ...item, quantity }
                    : item
            )
        );
    }

    function clearUserCart() {
        setCart([]);
        clearCart();
    }

    const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,

                login,
                logout,
                register,
                requestPasswordReset,
                verifyResetCode,
                completePasswordReset,

                searchBar,
                setSearchBar,

                cart,
                cartCount,
                addToCart,
                removeFromCart,
                updateQuantity,
                clearUserCart,
            }}
        >
            {!loading && children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
